"use client"

import { useContext, useMemo } from "react"

import { useTranslations } from "next-intl"

import { PRICE_TIERS } from "@/components/marketplace/bnpl/price-tiers"
import { moneyShort } from "@/components/marketplace/format"
import { DidYouMean } from "@/components/marketplace/listing/did-you-mean"
import { ListingEmptyHeadingContext } from "@/components/marketplace/listing/listing-shell"
import type { FacetOption } from "@/components/marketplace/listing/types"
import { mapSearchProduct } from "@/components/marketplace/map-search-product"
import { ProductCard } from "@/components/marketplace/product/product-card"
import { Button, ChevronIcon, CloseIcon, Heading, Pill, Reel, Stack, Text } from "@/components/ui"
import { Link, useRouter } from "@/i18n/navigation"
import { useSearchData } from "@/lib/hooks/search/use-search-data"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"
import { facetValueLabel, withAttribute } from "@/lib/types/search"
import type { SearchFacet, SearchQuery } from "@/lib/types/search"

interface ListingEmptyProps {
  query: SearchQuery
  region: string
  categoryLabels?: Record<string, string>
  categoryOptions?: FacetOption[]
  facets?: SearchFacet[]
  onUpdateQuery: (updates: Partial<SearchQuery>) => void
  onClearAll: () => void
  categoriesHref: string
  homeHref: string
  suggestion?: string
  onSuggestionSelect?: (term: string) => void
}

const RELAXED_LIMIT = 12

export const ListingEmpty = ({
  query,
  region,
  categoryLabels,
  categoryOptions,
  facets,
  onUpdateQuery,
  onClearAll,
  categoriesHref,
  homeHref,
  suggestion,
  onSuggestionSelect,
}: ListingEmptyProps) => {
  const t = useTranslations("search")
  const tCommon = useTranslations("common")
  const router = useRouter()
  const headingLevel = useContext(ListingEmptyHeadingContext)

  const budgetLabel = useMemo(() => {
    if (query.priceMin === undefined && query.priceMax === undefined) return null
    const tier = PRICE_TIERS.find((entry) => entry.min === query.priceMin && entry.max === query.priceMax)
    if (tier) return t("priceBand.band", { band: tier.label })
    const low = query.priceMin !== undefined ? moneyShort(query.priceMin) : null
    const high = query.priceMax !== undefined ? moneyShort(query.priceMax) : null
    return low && high ? `${low} – ${high}` : low ? t("activeFilters.priceFrom", { low }) : t("activeFilters.priceTo", { high: high ?? "" })
  }, [query.priceMin, query.priceMax, t])

  const tokens = [
    ...query.categories.map((id) => ({
      key: `cat-${id}`,
      label: categoryLabels?.[id] ?? t("activeFilters.categoryFallback"),
      onRemove: () => onUpdateQuery({ categories: query.categories.filter((value) => value !== id) }),
    })),
    ...Object.entries(query.attributes).flatMap(([key, values]) =>
      values.map((value) => ({
        key: `${key}-${value}`,
        label: facetValueLabel(facets, key, value),
        onRemove: () =>
          onUpdateQuery({
            attributes: withAttribute(
              query.attributes,
              key,
              values.filter((entry) => entry !== value)
            ),
          }),
      }))
    ),
    ...(budgetLabel ? [{ key: "budget", label: budgetLabel, onRemove: () => onUpdateQuery({ priceMin: undefined, priceMax: undefined }) }] : []),
    ...(query.inStockOnly !== false ? [{ key: "stock", label: t("inStockChip"), onRemove: () => onUpdateQuery({ inStockOnly: false }) }] : []),
  ]

  const relaxedQuery = useMemo(
    (): SearchQuery => ({ ...query, text: "", attributes: {}, priceMin: undefined, priceMax: undefined, inStockOnly: false, sort: "relevance", limit: RELAXED_LIMIT }),
    [query]
  )
  const { data: alternatives } = useSearchData(relaxedQuery, 1)
  const products = useMemo(() => (alternatives?.products ?? []).map((product) => mapSearchProduct(product, region)), [alternatives, region])
  const { ref: alternativesRef, atEnd: alternativesAtEnd } = useReelPagination(products.length)

  const departments = categoryOptions ?? []
  const current = departments.find((option) => query.categories.some((id) => id === option.value || (option.children ?? []).some((child) => child.value === id)))
  const siblings = (current ? [current, ...departments.filter((option) => option !== current)] : departments)
    .flatMap((option) => [option, ...(option.children ?? [])])
    .filter((option) => !query.categories.includes(option.value))
    .slice(0, 6)

  const goBack = () => {
    if (window.history.length > 1) router.back()
    else router.push(homeHref)
  }

  return (
    <Stack gap="600" className="pb-600">
      <div>
        {headingLevel === "h1" && (
          <Button variant="quiet" onClick={goBack} iconLeft={<ChevronIcon size={16} className="rotate-180" />} className="text-body @3xl:text-body-lg font-normal underline">
            {tCommon("goBack")}
          </Button>
        )}
        <Stack gap="250" className={`max-w-measure ${headingLevel === "h1" ? "mt-300" : ""}`}>
          {suggestion && <DidYouMean suggestion={suggestion} onSelect={onSuggestionSelect} />}
          <Heading as={headingLevel} variant="md" tone="strong" balance className="text-section-head">
            {query.text ? t("empty.titleFor", { query: query.text }) : t("empty.noResultsFiltered")}
          </Heading>
          <Text as="p" variant="body" tone="muted" balance className="@3xl:text-body-lg">
            {tokens.length > 0 ? t("empty.bodyFiltered") : t("empty.body")}
          </Text>
        </Stack>
      </div>

      {tokens.length > 0 && (
        <Stack gap="250">
          <Text as="h3" variant="label" tone="subtle">
            {t("empty.removeOne")}
          </Text>
          <div className="flex flex-wrap items-center gap-200">
            {tokens.map((token) => (
              <Pill
                key={token.key}
                tone="brand"
                size="sm"
                onClick={token.onRemove}
                className="border-line-brand border pr-100 font-bold"
                aria-label={t("activeFilters.removeAria", { label: token.label })}
              >
                {token.label}
                <span className="bg-brand/15 text-fg-brand flex size-4.5 shrink-0 items-center justify-center rounded-full">
                  <CloseIcon size={11} />
                </span>
              </Pill>
            ))}
            {tokens.length > 1 && (
              <Button variant="quiet" onClick={onClearAll} className="text-fg-muted text-caption-lg shrink-0 font-semibold underline">
                {t("activeFilters.clearAll")}
              </Button>
            )}
          </div>
        </Stack>
      )}

      {products.length > 0 && (
        <Stack gap="350">
          <Text as="h3" variant="label" tone="subtle">
            {t("empty.alternativesTitle")}
          </Text>
          <Reel ref={alternativesRef} gap="300" className={`mx-gut-bleed px-gut ${alternativesAtEnd ? "" : "mask-r-from-92%"}`}>
            {products.map((product) => (
              <div key={product.id} className="w-36 shrink-0 @3xl:w-44">
                <ProductCard product={product} size="compact" />
              </div>
            ))}
          </Reel>
        </Stack>
      )}

      {siblings.length > 0 && (
        <Stack gap="250">
          <Text as="h3" variant="label" tone="subtle">
            {t("empty.categoriesTitle")}
          </Text>
          <div className="flex flex-wrap gap-200">
            {siblings.map((option) => (
              <Pill
                key={option.value}
                tone="outline"
                size="sm"
                onClick={() => onUpdateQuery({ categories: [option.value], text: "", attributes: {}, priceMin: undefined, priceMax: undefined, inStockOnly: true })}
              >
                {option.label}
              </Pill>
            ))}
          </div>
        </Stack>
      )}

      <div>
        <Button asChild className="shadow-cta">
          <Link href={categoriesHref}>{t("empty.keepShopping")}</Link>
        </Button>
      </div>
    </Stack>
  )
}
